import type { ReactNode } from "react";

export interface OverlayState {
  childComponent: ReactNode;
  open: boolean;
}

export interface RecipeListItem {
  id: number;
  recipe_name: string | null;
  recipe_number: string | null;
}

export interface PaginatedRecipes {
  data: RecipeListItem[];
  total?: number;
  current_page?: number;
  per_page?: number;
}

export interface RecipeFormValues {
  id: number;
  recipe_number: string;
  recipe_name: string;
  batch_size: string | number;
  weight_per_unit: string;
  unit_per_case: string;
  weight_per_case: string;
  manufacture_loss_percent: string | number;
  fermentation_loss_percent: string | number;
  cooking_loss_percent: string | number;
  slicing_loss_percent: string | number;
  manufacture_loss_calculated: string | number;
  fermentation_loss_calculated: string | number;
  cooking_loss_calculated: string | number;
  slicing_loss_calculated: string | number;
  target_production_carpan: string | number;
  labour: string | number;
  overhead: string | number;
  processed: string | number;
}

export function recipeToFormValues(
  recipe: Partial<RecipeFormValues> | null | undefined,
): RecipeFormValues {
  const source = recipe ?? {};
  const text = (value: unknown) => (value == null ? "" : String(value));
  const numberish = (value: unknown): string | number =>
    value == null || value === "" ? 0 : (value as string | number);

  return {
    id: Number(source.id ?? 0),
    recipe_number: text(source.recipe_number),
    recipe_name: text(source.recipe_name),
    batch_size: numberish(source.batch_size),
    weight_per_unit: text(source.weight_per_unit),
    unit_per_case: text(source.unit_per_case),
    weight_per_case: text(source.weight_per_case),
    manufacture_loss_percent: numberish(source.manufacture_loss_percent),
    fermentation_loss_percent: numberish(source.fermentation_loss_percent),
    cooking_loss_percent: numberish(source.cooking_loss_percent),
    slicing_loss_percent: numberish(source.slicing_loss_percent),
    manufacture_loss_calculated: numberish(source.manufacture_loss_calculated),
    fermentation_loss_calculated: numberish(
      source.fermentation_loss_calculated,
    ),
    cooking_loss_calculated: numberish(source.cooking_loss_calculated),
    slicing_loss_calculated: numberish(source.slicing_loss_calculated),
    target_production_carpan: numberish(source.target_production_carpan),
    labour: numberish(source.labour),
    overhead: numberish(source.overhead),
    processed: numberish(source.processed),
  };
}

export const emptyRecipeForm: RecipeFormValues = {
  id: 0,
  recipe_number: "",
  recipe_name: "",
  batch_size: 0,
  weight_per_unit: "",
  unit_per_case: "",
  weight_per_case: "",
  manufacture_loss_percent: 0,
  fermentation_loss_percent: 0,
  cooking_loss_percent: 0,
  slicing_loss_percent: 0,
  manufacture_loss_calculated: 0,
  fermentation_loss_calculated: 0,
  cooking_loss_calculated: 0,
  slicing_loss_calculated: 0,
  target_production_carpan: 0,
  labour: 0,
  overhead: 0,
  processed: 0,
};

export interface RecipeLineItem {
  id: number;
  recipe_id?: number;
  bom_id: string | number;
  bom_name: string;
  bom_plu: string;
  quantity: string | number;
  recipe_per_kg: string | number;
  unit_of_measure_id?: number | null;
  unit_of_measure_label?: string | null;
  product_type_id?: number | null;
  product_type_label?: string | null;
  is_packaging: number | boolean;
}

export interface BomOption {
  value: number;
  label: string;
  is_food_contact?: boolean;
}

export interface BomItem {
  id: number;
  name: string;
  plu: string;
  description?: string | null;
  unit_of_measure: { value: number; label: string };
  product_type: BomOption;
  product_types_value?: number | string | null;
  product_types_label?: string | null;
}

export interface PaginatedBomItems {
  data: BomItem[];
  total?: number;
}

export interface AddIngredientPayload {
  recipe_id: number;
  bom_id: number;
  bom_name: string;
  bom_plu: string;
  description?: string | null;
  unit_of_measure: { value: number; label: string };
  product_types_value?: number | string | null;
  product_types_label?: string | null;
  product_type: BomOption;
  quantity: string | number;
  is_packaging: boolean;
}

export interface FetchedRecipe {
  recipe: RecipeFormValues;
  recipeItems: RecipeLineItem[];
  packagingItems: RecipeLineItem[];
}

export interface RecipeMutationResponse {
  recipe: RecipeFormValues;
  message: string;
}

export interface MessageResponse {
  message: string;
}

export interface RecipeListQuery {
  search: string | null;
  page: number;
}
